/**
 * Travel Directory Page
 * Comprehensive list of Travel Agencies & Airlines worldwide
 * Users can book flights 24/7 using their ParachPay Card
 */
import { useState, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plane, 
  Building2, 
  Globe, 
  Search, 
  ExternalLink, 
  MapPin,
  CreditCard,
  Clock
} from 'lucide-react';
import { motion } from 'framer-motion';

// Comprehensive Travel Directory Data
const travelDirectory = {
  "North America": {
    "United States": {
      airlines: [
        { name: "American Airlines", city: "Fort Worth, TX", address: "1 Skyview Dr, Fort Worth, TX 76155", url: "https://www.aa.com", type: "airline" },
        { name: "Delta Air Lines", city: "Atlanta, GA", address: "1030 Delta Blvd, Atlanta, GA 30354", url: "https://www.delta.com", type: "airline" },
        { name: "United Airlines", city: "Chicago, IL", address: "233 S Wacker Dr, Chicago, IL 60606", url: "https://www.united.com", type: "airline" },
        { name: "Southwest Airlines", city: "Dallas, TX", address: "2702 Love Field Dr, Dallas, TX 75235", url: "https://www.southwest.com", type: "airline" },
        { name: "JetBlue Airways", city: "New York, NY", address: "27-01 Queens Plaza North, Long Island City, NY 11101", url: "https://www.jetblue.com", type: "airline" },
        { name: "Alaska Airlines", city: "Seattle, WA", address: "19300 International Blvd, Seattle, WA 98188", url: "https://www.alaskaair.com", type: "airline" },
        { name: "Spirit Airlines", city: "Miramar, FL", address: "2800 Executive Way, Miramar, FL 33025", url: "https://www.spirit.com", type: "airline" },
        { name: "Frontier Airlines", city: "Denver, CO", address: "4545 Airport Way, Denver, CO 80239", url: "https://www.flyfrontier.com", type: "airline" }
      ],
      agencies: [
        { name: "Expedia", city: "Seattle, WA", address: "1111 Expedia Group Way W, Seattle, WA 98119", url: "https://www.expedia.com", type: "agency" },
        { name: "Booking.com (US)", city: "New York, NY", address: "75 Varick St, New York, NY 10013", url: "https://www.booking.com", type: "agency" },
        { name: "Priceline", city: "Norwalk, CT", address: "800 Connecticut Ave, Norwalk, CT 06854", url: "https://www.priceline.com", type: "agency" },
        { name: "Orbitz", city: "Chicago, IL", address: "500 W Madison St, Chicago, IL 60661", url: "https://www.orbitz.com", type: "agency" },
        { name: "Travelocity", city: "Dallas, TX", address: "5400 LBJ Freeway, Dallas, TX 75240", url: "https://www.travelocity.com", type: "agency" },
        { name: "CheapOair", city: "New York, NY", address: "135 W 50th St, New York, NY 10020", url: "https://www.cheapoair.com", type: "agency" },
        { name: "Kayak", city: "Stamford, CT", address: "7 Market St, Stamford, CT 06902", url: "https://www.kayak.com", type: "agency" }
      ]
    },
    "Canada": {
      airlines: [
        { name: "Air Canada", city: "Montreal, QC", address: "7373 Côte-Vertu Blvd W, Saint-Laurent, QC H4S 1Z3", url: "https://www.aircanada.com", type: "airline" },
        { name: "WestJet", city: "Calgary, AB", address: "22 Aerial Place NE, Calgary, AB T2E 3J1", url: "https://www.westjet.com", type: "airline" },
        { name: "Porter Airlines", city: "Toronto, ON", address: "Billy Bishop Toronto City Airport, Toronto, ON M5V 1A1", url: "https://www.flyporter.com", type: "airline" },
        { name: "Flair Airlines", city: "Edmonton, AB", address: "Edmonton International Airport, Edmonton, AB T9E 0V3", url: "https://www.flyflair.com", type: "airline" }
      ],
      agencies: [
        { name: "Flight Centre Canada", city: "Vancouver, BC", address: "1500-1055 Dunsmuir St, Vancouver, BC V7X 1L4", url: "https://www.flightcentre.ca", type: "agency" },
        { name: "Expedia Canada", city: "Toronto, ON", address: "123 Front St W, Toronto, ON M5J 2M2", url: "https://www.expedia.ca", type: "agency" },
        { name: "Red Tag Vacations", city: "Mississauga, ON", address: "5450 Explorer Dr, Mississauga, ON L4W 5N1", url: "https://www.redtag.ca", type: "agency" }
      ]
    },
    "Mexico": {
      airlines: [
        { name: "Aeroméxico", city: "Mexico City", address: "Paseo de la Reforma 445, Cuauhtémoc, 06500 CDMX", url: "https://www.aeromexico.com", type: "airline" },
        { name: "Volaris", city: "Mexico City", address: "Antonio Dovalí Jaime 70, Santa Fe, 01210 CDMX", url: "https://www.volaris.com", type: "airline" },
        { name: "VivaAerobus", city: "Monterrey", address: "Av. Miguel Alemán 88, Monterrey, N.L. 64000", url: "https://www.vivaaerobus.com", type: "airline" }
      ],
      agencies: [
        { name: "Despegar Mexico", city: "Mexico City", address: "Insurgentes Sur 1602, Benito Juárez, 03940 CDMX", url: "https://www.despegar.com.mx", type: "agency" },
        { name: "Best Day Travel", city: "Cancun", address: "Blvd. Kukulcan KM 9, Zona Hotelera, Cancún, Q.R.", url: "https://www.bestday.com", type: "agency" }
      ]
    }
  },
  "Europe": {
    "United Kingdom": {
      airlines: [
        { name: "British Airways", city: "London", address: "Waterside, Harmondsworth, UB7 0GB", url: "https://www.britishairways.com", type: "airline" },
        { name: "Virgin Atlantic", city: "Crawley", address: "The VHQ, Fleming Way, Crawley RH10 9DF", url: "https://www.virginatlantic.com", type: "airline" },
        { name: "easyJet", city: "Luton", address: "Hangar 89, London Luton Airport, LU2 9PF", url: "https://www.easyjet.com", type: "airline" },
        { name: "Ryanair UK", city: "London", address: "Ryanair UK Ltd, London Stansted Airport, CM24 1QW", url: "https://www.ryanair.com", type: "airline" },
        { name: "Jet2", city: "Leeds", address: "Low Fare Finder House, Leeds LS11 5RY", url: "https://www.jet2.com", type: "airline" }
      ],
      agencies: [
        { name: "Skyscanner UK", city: "Edinburgh", address: "Quartermile One, 15 Lauriston Place, Edinburgh EH3 9EN", url: "https://www.skyscanner.net", type: "agency" },
        { name: "lastminute.com", city: "London", address: "30 The South Colonnade, Canary Wharf, London E14 5EP", url: "https://www.lastminute.com", type: "agency" },
        { name: "TUI UK", city: "Luton", address: "Wigmore House, Wigmore Lane, Luton LU2 9TN", url: "https://www.tui.co.uk", type: "agency" },
        { name: "Thomas Cook", city: "Peterborough", address: "Westbrook, Peterborough PE3 7YA", url: "https://www.thomascook.com", type: "agency" }
      ]
    },
    "Germany": {
      airlines: [
        { name: "Lufthansa", city: "Frankfurt", address: "Lufthansa Aviation Center, Frankfurt 60546", url: "https://www.lufthansa.com", type: "airline" },
        { name: "Eurowings", city: "Düsseldorf", address: "Eurowings GmbH, Düsseldorf Airport, 40474", url: "https://www.eurowings.com", type: "airline" },
        { name: "Condor", city: "Frankfurt", address: "Condor Platz, 60549 Frankfurt", url: "https://www.condor.com", type: "airline" }
      ],
      agencies: [
        { name: "Check24", city: "Munich", address: "Erika-Mann-Straße 62, 80636 München", url: "https://www.check24.de", type: "agency" },
        { name: "Ab-in-den-Urlaub", city: "Leipzig", address: "Tröndlinring 7, 04105 Leipzig", url: "https://www.ab-in-den-urlaub.de", type: "agency" },
        { name: "FTI Touristik", city: "Munich", address: "Landsberger Str. 88, 80339 München", url: "https://www.fti.de", type: "agency" }
      ]
    },
    "France": {
      airlines: [
        { name: "Air France", city: "Paris", address: "45 Rue de Paris, 95747 Roissy CDG", url: "https://www.airfrance.com", type: "airline" },
        { name: "Transavia France", city: "Paris", address: "3 Allée Hélène Boucher, 91550 Paray-Vieille-Poste", url: "https://www.transavia.com", type: "airline" },
        { name: "Corsair International", city: "Paris", address: "2 Avenue Charles Lindbergh, 94150 Rungis", url: "https://www.corsair.fr", type: "airline" }
      ],
      agencies: [
        { name: "Opodo France", city: "Paris", address: "6 Place de la Madeleine, 75008 Paris", url: "https://www.opodo.fr", type: "agency" },
        { name: "Promovacances", city: "Paris", address: "17 Rue de Châteaudun, 75009 Paris", url: "https://www.promovacances.com", type: "agency" },
        { name: "Voyages SNCF", city: "Paris", address: "2 Place de la Défense, 92400 Courbevoie", url: "https://www.oui.sncf", type: "agency" }
      ]
    },
    "Netherlands": {
      airlines: [
        { name: "KLM Royal Dutch Airlines", city: "Amsterdam", address: "Amsterdamseweg 55, 1182 GP Amstelveen", url: "https://www.klm.com", type: "airline" },
        { name: "Transavia", city: "Schiphol", address: "Piet Guilonardweg 15, 1117 EE Schiphol", url: "https://www.transavia.com", type: "airline" }
      ],
      agencies: [
        { name: "Booking.com", city: "Amsterdam", address: "Herengracht 597, 1017 CE Amsterdam", url: "https://www.booking.com", type: "agency" },
        { name: "Vliegtickets.nl", city: "Amsterdam", address: "Keizersgracht 62, 1015 CS Amsterdam", url: "https://www.vliegtickets.nl", type: "agency" },
        { name: "Corendon", city: "Lijnden", address: "Melbournestraat 10, 1175 RM Lijnden", url: "https://www.corendon.nl", type: "agency" }
      ]
    },
    "Spain": {
      airlines: [
        { name: "Iberia", city: "Madrid", address: "Calle Velázquez 130, 28006 Madrid", url: "https://www.iberia.com", type: "airline" },
        { name: "Vueling", city: "Barcelona", address: "Parque de Negocios Mas Blau II, 08820 El Prat", url: "https://www.vueling.com", type: "airline" },
        { name: "Air Europa", city: "Palma de Mallorca", address: "Calle Lluna 2, 07011 Palma", url: "https://www.aireuropa.com", type: "airline" }
      ],
      agencies: [
        { name: "eDreams Spain", city: "Barcelona", address: "Calle Bailén 67, 08009 Barcelona", url: "https://www.edreams.es", type: "agency" },
        { name: "Logitravel", city: "Palma de Mallorca", address: "Calle Gremi de Sabaters 21, 07009 Palma", url: "https://www.logitravel.com", type: "agency" },
        { name: "Viajes El Corte Inglés", city: "Madrid", address: "Calle Preciados 3, 28013 Madrid", url: "https://www.viajeselcorteingles.es", type: "agency" }
      ]
    },
    "Italy": {
      airlines: [
        { name: "ITA Airways", city: "Rome", address: "Via XX Settembre 97, 00187 Roma", url: "https://www.ita-airways.com", type: "airline" },
        { name: "Neos Air", city: "Milan", address: "Centro Direzionale Milanofiori, 20090 Assago", url: "https://www.neosair.it", type: "airline" }
      ],
      agencies: [
        { name: "Volagratis", city: "Milan", address: "Via San Gregorio 6, 20124 Milano", url: "https://www.volagratis.com", type: "agency" },
        { name: "Lastminute.com Italy", city: "Milan", address: "Corso Buenos Aires 54, 20124 Milano", url: "https://www.lastminute.com/it", type: "agency" },
        { name: "Alpitour", city: "Turin", address: "Via Lugaro 15, 10126 Torino", url: "https://www.alpitour.it", type: "agency" }
      ]
    },
    "Switzerland": {
      airlines: [
        { name: "Swiss International Air Lines", city: "Zurich", address: "SWISS, P.O. Box, 8058 Zürich-Flughafen", url: "https://www.swiss.com", type: "airline" },
        { name: "Edelweiss Air", city: "Zurich", address: "Flughofstrasse 57, 8152 Opfikon", url: "https://www.flyedelweiss.com", type: "airline" }
      ],
      agencies: [
        { name: "Hotelplan Suisse", city: "Zurich", address: "Sägereistrasse 20, 8152 Glattbrugg", url: "https://www.hotelplan.ch", type: "agency" },
        { name: "Kuoni Reisen", city: "Zurich", address: "Neue Hard 7, 8005 Zürich", url: "https://www.kuoni.ch", type: "agency" }
      ]
    },
    "Portugal": {
      airlines: [
        { name: "TAP Air Portugal", city: "Lisbon", address: "Aeroporto de Lisboa, 1704-801 Lisboa", url: "https://www.flytap.com", type: "airline" }
      ],
      agencies: [
        { name: "Viagens Abreu", city: "Porto", address: "Avenida dos Aliados 207, 4000-067 Porto", url: "https://www.abreu.pt", type: "agency" },
        { name: "Top Atlântico", city: "Lisbon", address: "Avenida da Liberdade 36, 1250-145 Lisboa", url: "https://www.topatlantico.pt", type: "agency" }
      ]
    }
  },
  "Asia": {
    "United Arab Emirates": {
      airlines: [
        { name: "Emirates", city: "Dubai", address: "Emirates Group Headquarters, Dubai", url: "https://www.emirates.com", type: "airline" },
        { name: "Etihad Airways", city: "Abu Dhabi", address: "Etihad Airways Centre, Abu Dhabi", url: "https://www.etihad.com", type: "airline" },
        { name: "Flydubai", city: "Dubai", address: "Dubai International Airport, Terminal 2", url: "https://www.flydubai.com", type: "airline" },
        { name: "Air Arabia", city: "Sharjah", address: "Sharjah International Airport, Sharjah", url: "https://www.airarabia.com", type: "airline" }
      ],
      agencies: [
        { name: "Musafir.com", city: "Dubai", address: "Al Barsha 1, Dubai", url: "https://www.musafir.com", type: "agency" },
        { name: "Al Rostamani Travel", city: "Dubai", address: "Sheikh Zayed Road, Dubai", url: "https://www.alrostamanitravel.com", type: "agency" },
        { name: "DNATA Travel", city: "Dubai", address: "Airport Road, Dubai", url: "https://www.dnatatravel.com", type: "agency" }
      ]
    },
    "Singapore": {
      airlines: [
        { name: "Singapore Airlines", city: "Singapore", address: "Airline House, 25 Airline Road, Singapore 819829", url: "https://www.singaporeair.com", type: "airline" },
        { name: "Scoot", city: "Singapore", address: "Changi Airport Terminal 1, Singapore 918141", url: "https://www.flyscoot.com", type: "airline" },
        { name: "Jetstar Asia", city: "Singapore", address: "Changi Airport Terminal 1, Singapore", url: "https://www.jetstar.com", type: "airline" }
      ],
      agencies: [
        { name: "Chan Brothers Travel", city: "Singapore", address: "452 North Bridge Road, Singapore 188733", url: "https://www.chanbrothers.com", type: "agency" },
        { name: "EU Holidays", city: "Singapore", address: "1 Park Road, #02-62 People's Park Complex", url: "https://www.euholidays.com.sg", type: "agency" }
      ]
    },
    "Japan": {
      airlines: [
        { name: "Japan Airlines (JAL)", city: "Tokyo", address: "2-4-11 Higashi-Shinagawa, Shinagawa-ku, Tokyo", url: "https://www.jal.co.jp", type: "airline" },
        { name: "All Nippon Airways (ANA)", city: "Tokyo", address: "Shiodome City Center, 1-5-2 Higashi-Shinbashi, Minato-ku", url: "https://www.ana.co.jp", type: "airline" },
        { name: "Peach Aviation", city: "Osaka", address: "Kansai International Airport, Osaka", url: "https://www.flypeach.com", type: "airline" },
        { name: "Skymark Airlines", city: "Tokyo", address: "Haneda Airport, Tokyo 144-0041", url: "https://www.skymark.co.jp", type: "airline" }
      ],
      agencies: [
        { name: "JTB Japan", city: "Tokyo", address: "2-3-11 Higashishinagawa, Shinagawa-ku, Tokyo", url: "https://www.jtb.co.jp", type: "agency" },
        { name: "H.I.S. Travel", city: "Tokyo", address: "Shinjuku Sumitomo Bldg, 2-6-1 Nishi-Shinjuku", url: "https://www.his-j.com", type: "agency" }
      ]
    },
    "China": {
      airlines: [
        { name: "Air China", city: "Beijing", address: "30 Tianzhu Road, Airport Industrial Zone, Beijing", url: "https://www.airchina.com", type: "airline" },
        { name: "China Eastern Airlines", city: "Shanghai", address: "2550 Hongqiao Road, Shanghai 200335", url: "https://www.ceair.com", type: "airline" },
        { name: "China Southern Airlines", city: "Guangzhou", address: "278 Jichang Road, Baiyun District, Guangzhou", url: "https://www.csair.com", type: "airline" },
        { name: "Hainan Airlines", city: "Haikou", address: "29 Haixiu Road, Haikou, Hainan", url: "https://www.hainanairlines.com", type: "airline" }
      ],
      agencies: [
        { name: "Ctrip (Trip.com)", city: "Shanghai", address: "99 Fu Quan Road, Shanghai 200335", url: "https://www.trip.com", type: "agency" },
        { name: "Qunar", city: "Beijing", address: "Qunar Building, Zhongguancun, Beijing", url: "https://www.qunar.com", type: "agency" },
        { name: "Fliggy (Alibaba)", city: "Hangzhou", address: "Alibaba Campus, Hangzhou", url: "https://www.fliggy.com", type: "agency" }
      ]
    },
    "India": {
      airlines: [
        { name: "Air India", city: "New Delhi", address: "Airlines House, 113 Gurudwara Rakabganj Road, New Delhi", url: "https://www.airindia.in", type: "airline" },
        { name: "IndiGo", city: "Gurgaon", address: "Level 1, Tower C, Global Business Park, Gurgaon", url: "https://www.goindigo.in", type: "airline" },
        { name: "SpiceJet", city: "Gurgaon", address: "319 Udyog Vihar Phase IV, Gurgaon 122015", url: "https://www.spicejet.com", type: "airline" },
        { name: "Vistara", city: "Gurgaon", address: "Vatika Business Park, Sohna Road, Gurgaon", url: "https://www.airvistara.com", type: "airline" },
        { name: "Go First", city: "Mumbai", address: "1st Floor, Sahar Cargo, Mumbai 400099", url: "https://www.flygofirst.com", type: "airline" }
      ],
      agencies: [
        { name: "MakeMyTrip", city: "Gurgaon", address: "19th Floor, Building No. 5, Tower A, DLF Cyber City", url: "https://www.makemytrip.com", type: "agency" },
        { name: "Yatra", city: "Gurgaon", address: "Unitech Infospace, Old Delhi-Gurgaon Road, Gurgaon", url: "https://www.yatra.com", type: "agency" },
        { name: "Cleartrip", city: "Mumbai", address: "Embassy 247, LBS Marg, Vikhroli, Mumbai", url: "https://www.cleartrip.com", type: "agency" },
        { name: "Goibibo", city: "Gurgaon", address: "Building No. 5, Tower A, DLF Cyber City, Gurgaon", url: "https://www.goibibo.com", type: "agency" }
      ]
    },
    "Thailand": {
      airlines: [
        { name: "Thai Airways", city: "Bangkok", address: "89 Vibhavadi Rangsit Road, Bangkok 10900", url: "https://www.thaiairways.com", type: "airline" },
        { name: "Bangkok Airways", city: "Bangkok", address: "99 Mu 14, Vibhavadee Rangsit Road, Bangkok", url: "https://www.bangkokair.com", type: "airline" },
        { name: "Thai AirAsia", city: "Bangkok", address: "Don Mueang International Airport, Bangkok", url: "https://www.airasia.com", type: "airline" },
        { name: "Nok Air", city: "Bangkok", address: "3 Rajanakarn Building, South Sathorn Road, Bangkok", url: "https://www.nokair.com", type: "airline" }
      ],
      agencies: [
        { name: "12Go Asia", city: "Bangkok", address: "2029 Bangna-Trad Road, Bangkok 10260", url: "https://www.12go.asia", type: "agency" },
        { name: "Traveloka Thailand", city: "Bangkok", address: "Silom Complex, Bangkok", url: "https://www.traveloka.com", type: "agency" }
      ]
    },
    "South Korea": {
      airlines: [
        { name: "Korean Air", city: "Seoul", address: "260 Haneul-gil, Gangseo-gu, Seoul", url: "https://www.koreanair.com", type: "airline" },
        { name: "Asiana Airlines", city: "Seoul", address: "47 Osoe-dong, Gangseo-gu, Seoul", url: "https://www.flyasiana.com", type: "airline" },
        { name: "Jeju Air", city: "Seoul", address: "76 Gonghang-daero, Gangseo-gu, Seoul", url: "https://www.jejuair.net", type: "airline" },
        { name: "Jin Air", city: "Seoul", address: "260 Haneul-gil, Gangseo-gu, Seoul", url: "https://www.jinair.com", type: "airline" }
      ],
      agencies: [
        { name: "Interpark Tour", city: "Seoul", address: "Gangnam Finance Center, Yeoksam-dong, Seoul", url: "https://www.interparkglobal.com", type: "agency" },
        { name: "Hana Tour", city: "Seoul", address: "369 Apgujeong-ro, Gangnam-gu, Seoul", url: "https://www.hanatour.com", type: "agency" }
      ]
    },
    "Malaysia": {
      airlines: [
        { name: "Malaysia Airlines", city: "Kuala Lumpur", address: "MAS Building, Sultan Abdul Aziz Shah Airport", url: "https://www.malaysiaairlines.com", type: "airline" },
        { name: "AirAsia", city: "Kuala Lumpur", address: "RedQ, Jalan Pekeliling 5, KLIA", url: "https://www.airasia.com", type: "airline" },
        { name: "Malindo Air", city: "Kuala Lumpur", address: "KLIA2, KLIA Sepang, Selangor", url: "https://www.malindoair.com", type: "airline" }
      ],
      agencies: [
        { name: "Traveloka Malaysia", city: "Kuala Lumpur", address: "Menara CIMB, Jalan Stesen Sentral 2", url: "https://www.traveloka.com/en-my", type: "agency" },
        { name: "Easybook", city: "Kuala Lumpur", address: "Wisma Cosway, Jalan Raja Chulan", url: "https://www.easybook.com", type: "agency" }
      ]
    }
  },
  "Middle East": {
    "Qatar": {
      airlines: [
        { name: "Qatar Airways", city: "Doha", address: "Qatar Airways Tower, Airport Road, Doha", url: "https://www.qatarairways.com", type: "airline" }
      ],
      agencies: [
        { name: "Qatar Holidays", city: "Doha", address: "Souq Waqif, Doha", url: "https://www.qatarairways.com/holidays", type: "agency" },
        { name: "Orient Tours", city: "Doha", address: "Al Sadd, Doha", url: "https://www.orienttours.com", type: "agency" }
      ]
    },
    "Saudi Arabia": {
      airlines: [
        { name: "Saudia", city: "Jeddah", address: "Saudia City, Jeddah 21231", url: "https://www.saudia.com", type: "airline" },
        { name: "Flynas", city: "Riyadh", address: "King Khalid International Airport, Riyadh", url: "https://www.flynas.com", type: "airline" }
      ],
      agencies: [
        { name: "Al Tayyar Travel", city: "Riyadh", address: "Olaya Street, Riyadh 11614", url: "https://www.altayyargroup.com", type: "agency" },
        { name: "Almosafer", city: "Riyadh", address: "King Fahad Road, Riyadh", url: "https://www.almosafer.com", type: "agency" }
      ]
    },
    "Israel": {
      airlines: [
        { name: "El Al Israel Airlines", city: "Tel Aviv", address: "Ben Gurion International Airport, 7015001", url: "https://www.elal.com", type: "airline" },
        { name: "Israir", city: "Tel Aviv", address: "Ben Gurion Airport, Tel Aviv", url: "https://www.israir.co.il", type: "airline" }
      ],
      agencies: [
        { name: "Eshet Tours", city: "Tel Aviv", address: "144 Menachem Begin Road, Tel Aviv", url: "https://www.eshet.com", type: "agency" },
        { name: "Ophir Tours", city: "Tel Aviv", address: "21 Carlibach Street, Tel Aviv", url: "https://www.ophirtours.co.il", type: "agency" }
      ]
    },
    "Turkey": {
      airlines: [
        { name: "Turkish Airlines", city: "Istanbul", address: "Atatürk Havalimanı, Yeşilköy, Istanbul", url: "https://www.turkishairlines.com", type: "airline" },
        { name: "Pegasus Airlines", city: "Istanbul", address: "Aeropark, Sabiha Gökçen Airport, Istanbul", url: "https://www.flypgs.com", type: "airline" },
        { name: "SunExpress", city: "Antalya", address: "Antalya Airport, 07230 Antalya", url: "https://www.sunexpress.com", type: "airline" }
      ],
      agencies: [
        { name: "Jolly Tur", city: "Istanbul", address: "Büyükdere Cad. 122, Şişli, Istanbul", url: "https://www.jollytur.com", type: "agency" },
        { name: "ETS Tur", city: "Istanbul", address: "Nispetiye Cad. Akmerkez Residence, Etiler, Istanbul", url: "https://www.etstur.com", type: "agency" }
      ]
    }
  },
  "Africa": {
    "South Africa": {
      airlines: [
        { name: "South African Airways", city: "Johannesburg", address: "Airways Park, OR Tambo International Airport", url: "https://www.flysaa.com", type: "airline" },
        { name: "FlySafair", city: "Johannesburg", address: "Safair Operations, OR Tambo International Airport", url: "https://www.flysafair.co.za", type: "airline" },
        { name: "Kulula", city: "Johannesburg", address: "Lanseria Airport, Johannesburg", url: "https://www.kulula.com", type: "airline" },
        { name: "Airlink", city: "Johannesburg", address: "Greenstone Hill Office Park, Johannesburg", url: "https://www.flyairlink.com", type: "airline" }
      ],
      agencies: [
        { name: "Flight Centre South Africa", city: "Cape Town", address: "2 Long Street, Cape Town", url: "https://www.flightcentre.co.za", type: "agency" },
        { name: "Travelstart", city: "Cape Town", address: "The Colosseum, Century City, Cape Town", url: "https://www.travelstart.co.za", type: "agency" }
      ]
    },
    "Egypt": {
      airlines: [
        { name: "EgyptAir", city: "Cairo", address: "Cairo International Airport, Heliopolis, Cairo", url: "https://www.egyptair.com", type: "airline" },
        { name: "Air Cairo", city: "Cairo", address: "Cairo International Airport, Terminal 1", url: "https://www.flyaircairo.com", type: "airline" }
      ],
      agencies: [
        { name: "Travco Group", city: "Cairo", address: "26 Sphinx Square, Mohandeseen, Cairo", url: "https://www.travcohotels.com", type: "agency" },
        { name: "Thomas Cook Egypt", city: "Cairo", address: "12 Mahmoud Bassiouny St, Cairo", url: "https://www.thomascookegypt.com", type: "agency" }
      ]
    },
    "Kenya": {
      airlines: [
        { name: "Kenya Airways", city: "Nairobi", address: "Airport North Road, Embakasi, Nairobi", url: "https://www.kenya-airways.com", type: "airline" },
        { name: "Jambojet", city: "Nairobi", address: "JKIA, Nairobi", url: "https://www.jambojet.com", type: "airline" }
      ],
      agencies: [
        { name: "Somak Safaris", city: "Nairobi", address: "Somak House, Mombasa Road, Nairobi", url: "https://www.somak.com", type: "agency" },
        { name: "Pollman's Tours", city: "Mombasa", address: "Moi Avenue, Mombasa", url: "https://www.pollmans.com", type: "agency" }
      ]
    },
    "Nigeria": {
      airlines: [
        { name: "Air Peace", city: "Lagos", address: "Murtala Muhammed Airport, Ikeja, Lagos", url: "https://www.flyairpeace.com", type: "airline" },
        { name: "Ibom Air", city: "Uyo", address: "Victor Attah International Airport, Uyo", url: "https://www.ibomair.com", type: "airline" },
        { name: "Arik Air", city: "Lagos", address: "Murtala Muhammed Airport 2, Lagos", url: "https://www.arikair.com", type: "airline" }
      ],
      agencies: [
        { name: "Wakanow", city: "Lagos", address: "35B Muri Okunola Street, Victoria Island, Lagos", url: "https://www.wakanow.com", type: "agency" },
        { name: "Jumia Travel", city: "Lagos", address: "King's Tower, Ozumba Mbadiwe Avenue, Lagos", url: "https://www.travel.jumia.com", type: "agency" }
      ]
    },
    "Morocco": {
      airlines: [
        { name: "Royal Air Maroc", city: "Casablanca", address: "Aéroport Mohammed V, Nouasseur, Casablanca", url: "https://www.royalairmaroc.com", type: "airline" },
        { name: "Air Arabia Maroc", city: "Casablanca", address: "Mohammed V Airport, Casablanca", url: "https://www.airarabia.com", type: "airline" }
      ],
      agencies: [
        { name: "Maroc Voyages", city: "Casablanca", address: "Boulevard Mohammed V, Casablanca", url: "https://www.marocvoyages.com", type: "agency" },
        { name: "Atlas Voyages", city: "Casablanca", address: "107 Avenue Hassan II, Casablanca", url: "https://www.atlasvoyages.com", type: "agency" }
      ]
    }
  },
  "Oceania": {
    "Australia": {
      airlines: [
        { name: "Qantas", city: "Sydney", address: "10 Bourke Road, Mascot, NSW 2020", url: "https://www.qantas.com", type: "airline" },
        { name: "Virgin Australia", city: "Brisbane", address: "275 Grey Street, South Brisbane QLD 4101", url: "https://www.virginaustralia.com", type: "airline" },
        { name: "Jetstar Australia", city: "Melbourne", address: "Melbourne Airport, VIC 3045", url: "https://www.jetstar.com", type: "airline" },
        { name: "Rex Airlines", city: "Sydney", address: "81 Baxter Road, Mascot NSW 2020", url: "https://www.rex.com.au", type: "airline" }
      ],
      agencies: [
        { name: "Flight Centre Australia", city: "Brisbane", address: "275 Grey Street, South Brisbane QLD 4101", url: "https://www.flightcentre.com.au", type: "agency" },
        { name: "Webjet", city: "Melbourne", address: "Level 2, 509 St Kilda Road, Melbourne", url: "https://www.webjet.com.au", type: "agency" },
        { name: "Helloworld Travel", city: "Melbourne", address: "179 Normanby Road, South Melbourne", url: "https://www.helloworld.com.au", type: "agency" }
      ]
    },
    "New Zealand": {
      airlines: [
        { name: "Air New Zealand", city: "Auckland", address: "185 Fanshawe Street, Auckland 1010", url: "https://www.airnewzealand.co.nz", type: "airline" },
        { name: "Jetstar New Zealand", city: "Auckland", address: "Auckland Airport, Auckland", url: "https://www.jetstar.com", type: "airline" }
      ],
      agencies: [
        { name: "Flight Centre NZ", city: "Auckland", address: "350 Queen Street, Auckland CBD", url: "https://www.flightcentre.co.nz", type: "agency" },
        { name: "House of Travel", city: "Wellington", address: "Level 5, 22 Willeston Street, Wellington", url: "https://www.houseoftravel.co.nz", type: "agency" }
      ]
    },
    "Fiji": {
      airlines: [
        { name: "Fiji Airways", city: "Suva", address: "Air Pacific House, 185 Victoria Parade, Suva", url: "https://www.fijiairways.com", type: "airline" }
      ],
      agencies: [
        { name: "Rosie Holidays", city: "Nadi", address: "Martintar, Nadi", url: "https://www.rosieholidays.com", type: "agency" },
        { name: "Tourist Transport Fiji", city: "Nadi", address: "Queens Highway, Nadi", url: "https://www.ttf.com.fj", type: "agency" }
      ]
    }
  },
  "South America": {
    "Brazil": {
      airlines: [
        { name: "LATAM Brasil", city: "São Paulo", address: "Rua Ática 673, São Paulo, SP 04634-042", url: "https://www.latam.com", type: "airline" },
        { name: "GOL Linhas Aéreas", city: "São Paulo", address: "Praça Comandante Linneu Gomes, Congonhas Airport", url: "https://www.voegol.com.br", type: "airline" },
        { name: "Azul Brazilian Airlines", city: "Barueri", address: "Av. Marcos Penteado de Ulhôa Rodrigues 939", url: "https://www.voeazul.com.br", type: "airline" }
      ],
      agencies: [
        { name: "Decolar", city: "São Paulo", address: "Av. das Nações Unidas 14171, São Paulo", url: "https://www.decolar.com", type: "agency" },
        { name: "CVC Brasil", city: "São Paulo", address: "Rua Líbero Badaró 293, São Paulo", url: "https://www.cvc.com.br", type: "agency" },
        { name: "Submarino Viagens", city: "São Paulo", address: "Av. das Nações Unidas, São Paulo", url: "https://www.submarinoviagens.com.br", type: "agency" }
      ]
    },
    "Argentina": {
      airlines: [
        { name: "Aerolíneas Argentinas", city: "Buenos Aires", address: "Bouchard 547, C1106ABG Buenos Aires", url: "https://www.aerolineas.com.ar", type: "airline" },
        { name: "Flybondi", city: "Buenos Aires", address: "El Palomar Airport, Buenos Aires", url: "https://www.flybondi.com", type: "airline" },
        { name: "JetSMART Argentina", city: "Buenos Aires", address: "Ezeiza International Airport, Buenos Aires", url: "https://www.jetsmart.com", type: "airline" }
      ],
      agencies: [
        { name: "Despegar Argentina", city: "Buenos Aires", address: "Av. Santa Fe 1701, Buenos Aires", url: "https://www.despegar.com.ar", type: "agency" },
        { name: "Almundo", city: "Buenos Aires", address: "Lavalle 835, Buenos Aires", url: "https://www.almundo.com.ar", type: "agency" }
      ]
    },
    "Chile": {
      airlines: [
        { name: "LATAM Chile", city: "Santiago", address: "Av. Américo Vespucio Sur 901, Las Condes, Santiago", url: "https://www.latam.com", type: "airline" },
        { name: "Sky Airline", city: "Santiago", address: "Av. Américo Vespucio Sur 901, Santiago", url: "https://www.skyairline.com", type: "airline" },
        { name: "JetSMART Chile", city: "Santiago", address: "Arturo Merino Benítez Airport, Santiago", url: "https://www.jetsmart.com", type: "airline" }
      ],
      agencies: [
        { name: "Despegar Chile", city: "Santiago", address: "Av. Providencia 1760, Santiago", url: "https://www.despegar.cl", type: "agency" },
        { name: "Turismo Cocha", city: "Santiago", address: "Av. El Bosque Norte 0177, Las Condes", url: "https://www.cocha.com", type: "agency" }
      ]
    },
    "Colombia": {
      airlines: [
        { name: "Avianca", city: "Bogotá", address: "Av. Calle 26 No. 59-15, Bogotá", url: "https://www.avianca.com", type: "airline" },
        { name: "LATAM Colombia", city: "Bogotá", address: "El Dorado International Airport, Bogotá", url: "https://www.latam.com", type: "airline" },
        { name: "Wingo", city: "Bogotá", address: "El Dorado Airport, Bogotá", url: "https://www.wingo.com", type: "airline" }
      ],
      agencies: [
        { name: "Despegar Colombia", city: "Bogotá", address: "Calle 100 No. 8A-49, Bogotá", url: "https://www.despegar.com.co", type: "agency" },
        { name: "Viajes Falabella", city: "Bogotá", address: "Calle 80 No. 11-42, Bogotá", url: "https://www.viajesfalabella.com.co", type: "agency" }
      ]
    },
    "Peru": {
      airlines: [
        { name: "LATAM Perú", city: "Lima", address: "Av. José Pardo 513, Miraflores, Lima", url: "https://www.latam.com", type: "airline" },
        { name: "Viva Air Perú", city: "Lima", address: "Jorge Chávez International Airport, Lima", url: "https://www.vivaair.com", type: "airline" }
      ],
      agencies: [
        { name: "Despegar Perú", city: "Lima", address: "Av. Camino Real 456, San Isidro, Lima", url: "https://www.despegar.com.pe", type: "agency" },
        { name: "Costamar Travel", city: "Lima", address: "Av. José Pardo 138, Miraflores, Lima", url: "https://www.costamar.com", type: "agency" }
      ]
    }
  }
};

const TravelDirectory = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContinent, setSelectedContinent] = useState('all');
  const [filterType, setFilterType] = useState('all');

  const continents = Object.keys(travelDirectory);

  // Filter and flatten all entries
  const filteredEntries = useMemo(() => {
    let entries = [];
    
    const continentsToSearch = selectedContinent === 'all' 
      ? continents 
      : [selectedContinent];
    
    continentsToSearch.forEach(continent => {
      const countries = travelDirectory[continent];
      Object.entries(countries).forEach(([country, data]) => {
        const allItems = [...(data.airlines || []), ...(data.agencies || [])];
        allItems.forEach(item => {
          entries.push({
            ...item,
            country,
            continent
          });
        });
      });
    });
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      entries = entries.filter(item => 
        item.name.toLowerCase().includes(query) ||
        item.city.toLowerCase().includes(query) ||
        item.country.toLowerCase().includes(query) ||
        item.continent.toLowerCase().includes(query)
      );
    }
    
    // Apply type filter
    if (filterType !== 'all') {
      entries = entries.filter(item => item.type === filterType);
    }
    
    return entries;
  }, [searchQuery, selectedContinent, filterType, continents]);

  // Count statistics
  const stats = useMemo(() => {
    let totalAirlines = 0;
    let totalAgencies = 0;
    let totalCountries = 0;
    
    Object.values(travelDirectory).forEach(countries => {
      totalCountries += Object.keys(countries).length;
      Object.values(countries).forEach(data => {
        totalAirlines += (data.airlines || []).length;
        totalAgencies += (data.agencies || []).length;
      });
    });
    
    return { totalAirlines, totalAgencies, totalCountries, totalContinents: continents.length };
  }, [continents]);

  return (
    <Layout>
      <motion.div 
        className="space-y-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="travel-directory-page"
      >
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-sm font-medium mb-4">
            <Plane className="w-4 h-4" />
            Global Travel Directory
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight mb-4">
            Travel Agencies & Airlines Directory
          </h1>
          <p className="text-lg text-muted-foreground">
            Discover airlines and travel agencies from every country. Book directly with the airline or agency of your choice.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6 text-center">
              <Plane className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
              <p className="text-3xl font-bold">{stats.totalAirlines}</p>
              <p className="text-sm text-muted-foreground">Airlines</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <Building2 className="w-8 h-8 mx-auto text-blue-600 mb-2" />
              <p className="text-3xl font-bold">{stats.totalAgencies}</p>
              <p className="text-sm text-muted-foreground">Travel Agencies</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <Globe className="w-8 h-8 mx-auto text-purple-600 mb-2" />
              <p className="text-3xl font-bold">{stats.totalCountries}</p>
              <p className="text-sm text-muted-foreground">Countries</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <CreditCard className="w-8 h-8 mx-auto text-orange-600 mb-2" />
              <p className="text-3xl font-bold">24/7</p>
              <p className="text-sm text-muted-foreground">Booking Available</p>
            </CardContent>
          </Card>
        </div>

        {/* ParachPay Card Banner */}
        <Card className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white border-0">
          <CardContent className="py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <CreditCard className="w-12 h-12" />
                <div>
                  <h3 className="text-xl font-bold">Plan Your Trip</h3>
                  <p className="text-emerald-100">Browse airlines and travel agencies, then book directly on their websites</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm bg-white/10 px-4 py-2 rounded-full">
                <Clock className="w-4 h-4" />
                Book Anytime, Anywhere
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search airlines, agencies, countries, or cities..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-xl"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant={filterType === 'all' ? 'default' : 'outline'}
              onClick={() => setFilterType('all')}
              className="rounded-xl"
            >
              All
            </Button>
            <Button
              variant={filterType === 'airline' ? 'default' : 'outline'}
              onClick={() => setFilterType('airline')}
              className="rounded-xl"
            >
              <Plane className="w-4 h-4 mr-2" />
              Airlines
            </Button>
            <Button
              variant={filterType === 'agency' ? 'default' : 'outline'}
              onClick={() => setFilterType('agency')}
              className="rounded-xl"
            >
              <Building2 className="w-4 h-4 mr-2" />
              Agencies
            </Button>
          </div>
        </div>

        {/* Continent Tabs */}
        <Tabs value={selectedContinent} onValueChange={setSelectedContinent} className="w-full">
          <TabsList className="flex flex-wrap h-auto gap-2 bg-transparent p-0 mb-6">
            <TabsTrigger 
              value="all" 
              className="rounded-xl data-[state=active]:bg-emerald-600 data-[state=active]:text-white"
            >
              All Regions
            </TabsTrigger>
            {continents.map(continent => (
              <TabsTrigger 
                key={continent} 
                value={continent}
                className="rounded-xl data-[state=active]:bg-emerald-600 data-[state=active]:text-white"
              >
                {continent}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Results */}
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Showing {filteredEntries.length} results
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredEntries.map((item, idx) => (
                <motion.div
                  key={`${item.name}-${idx}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.02 }}
                >
                  <Card className="h-full hover:shadow-lg transition-shadow">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2">
                          {item.type === 'airline' ? (
                            <div className="p-2 rounded-lg bg-emerald-500/10">
                              <Plane className="w-5 h-5 text-emerald-600" />
                            </div>
                          ) : (
                            <div className="p-2 rounded-lg bg-blue-500/10">
                              <Building2 className="w-5 h-5 text-blue-600" />
                            </div>
                          )}
                          <Badge variant="outline" className="text-xs">
                            {item.type === 'airline' ? 'Airline' : 'Agency'}
                          </Badge>
                        </div>
                        <Badge className="bg-muted text-muted-foreground text-xs">
                          {item.country}
                        </Badge>
                      </div>
                      
                      <h3 className="font-semibold text-lg mb-2">{item.name}</h3>
                      
                      <div className="space-y-2 text-sm text-muted-foreground mb-4">
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <span>{item.city}</span>
                        </div>
                        <p className="text-xs pl-6">{item.address}</p>
                      </div>
                      
                      <Button 
                        variant="outline" 
                        className="w-full rounded-xl"
                        asChild
                      >
                        <a 
                          href={item.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Visit Website
                        </a>
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
            
            {filteredEntries.length === 0 && (
              <Card className="text-center py-12">
                <CardContent>
                  <Globe className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground">No results found</p>
                  <p className="text-sm text-muted-foreground">Try adjusting your search or filters</p>
                </CardContent>
              </Card>
            )}
          </div>
        </Tabs>
      </motion.div>
    </Layout>
  );
};

export default TravelDirectory;
